import { add, dot, fromPolar, lerp, negate, perp, scale, segment, sub, vec } from '../geom';
import { formatLength, type LinearDimensionNode } from '../model';
import { arrowhead, extension, joined, textAlong } from './parts';
import type { DimensionDefinition } from './types';

export const LINEAR: DimensionDefinition<LinearDimensionNode> = {
  label: 'Linear dimension',
  arity: 7,
  fromRecord: ([ax, ay, bx, by, angle, offset, placed], kind) => ({
    kind,
    a: vec(ax!, ay!),
    b: vec(bx!, by!),
    angle: angle!,
    offset: placed ? offset! : null,
  }),
  resolve(node, look) {
    const toward = fromPolar(vec(0, 0), 1, node.angle);
    const along = dot(sub(node.b, node.a), toward);
    // Measured in the direction that runs from a toward b, so the offset's left is stable.
    const direction = along < 0 ? negate(toward) : toward;
    const left = perp(direction);
    const offset = node.offset ?? look.textHeight * 2 + look.arrowSize;
    const start = add(node.a, scale(left, offset));
    const end = add(start, scale(direction, Math.abs(along)));
    const value = Math.abs(along);
    const ends = joined([
      arrowhead(start, negate(direction), look),
      arrowhead(end, direction, look),
    ]);
    return {
      value,
      layout: {
        strokes: [
          segment(start, end),
          ...extension(node.a, start, look),
          ...extension(node.b, end, look),
          ...ends.strokes,
        ],
        fills: ends.fills,
        text: textAlong(lerp(start, end, 0.5), node.angle, formatLength(value, look.display), look),
      },
    };
  },
};
